import {
  Component, Input, Output, EventEmitter, ViewChild, ElementRef,
  AfterViewInit, ChangeDetectionStrategy, OnChanges, SimpleChanges,
  OnInit
} from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { SHARED_IMPORTS } from '../../shared/shared-imports';
import { OnEnterDirective } from '../../../directives/on-enter.directive';
import { OnEscapeDirective } from '../../../directives/on-escape.directive';

type Mode = 'select' | 'input';

@Component({
  selector: 'app-inline-editor',
  standalone: true,
  imports: [
   ...SHARED_IMPORTS,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    OnEnterDirective,
    OnEscapeDirective,
  ],
  templateUrl: 'components/shared/inline-editor/inline-editor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InlineEditorComponent implements AfterViewInit, OnChanges, OnInit {
  ngOnInit(): void {
  }
  @Input() set mode(value: 'select' | 'input' | 'text') {
    this._mode = value === 'text' ? 'input' : value;
  }
  get mode() { return this._mode; }
  private _mode: 'select' | 'input' = 'select';

  @Input() displayLabel?: string;
  @Input() options: any[] = [];
  @Input() groupBy?: string;
  @Input() displayProp: string = 'title';
  @Input() valueProp?: string | null = null;
  @Input() placeholder: string = '';
  @Input() value: any;
  @Input() openOnInit = false;
  @Output() confirm = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<void>();
  @Output() confirmed = new EventEmitter<any>();
  @Output() cancelled = new EventEmitter<void>();

  selected: any = null;
  textValue = '';

  @ViewChild('selectRef') selectRef?: MatSelect;
  @ViewChild('inputRef') inputRef?: ElementRef<HTMLInputElement>;

  // ---- memoization for grouped() to avoid heavy recompute churn ----
  private _lastOptionsRef: any[] | null = null;
  private _lastGroupBy?: string;
  private _lastDisplay?: string;
  private _groupedCache: { key: string; items: any[] }[] = [];

  ngAfterViewInit(): void {
    if (this.mode === 'select') {
      this.selected = this.value ?? null;
    } else {
      this.textValue = this.value ?? '';
      if (this.openOnInit && this.inputRef?.nativeElement) {
        setTimeout(() => this.inputRef?.nativeElement.focus(), 1000);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // clear memoized groups if inputs that affect grouping changed
    if (changes['options'] || changes['groupBy'] || changes['displayProp']) {
      this._lastOptionsRef = null;
    }
  }

  get grouped(): { key: string; items: any[] }[] {
    if (
      this._lastOptionsRef === this.options &&
      this._lastGroupBy === this.groupBy &&
      this._lastDisplay === this.displayProp
    ) {
      return this._groupedCache;
    }

    const opts = this.options || [];
    if (!this.groupBy) {
      this._groupedCache = [{ key: '', items: opts }];
    } else {
      const map = new Map<string, any[]>();
      for (const it of opts) {
        const k = (it?.[this.groupBy] ?? 'Other') as string;
        if (!map.has(k)) map.set(k, []);
        map.get(k)!.push(it);
      }
      for (const [, list] of map) {
        list.sort((a, b) =>
          String(a?.[this.displayProp] ?? '').localeCompare(String(b?.[this.displayProp] ?? ''))
        );
      }
      this._groupedCache = Array.from(map, ([key, items]) => ({ key, items }));
    }

    this._lastOptionsRef = this.options;
    this._lastGroupBy = this.groupBy;
    this._lastDisplay = this.displayProp;
    return this._groupedCache;
  }

  onConfirm(): void {
    if (this._mode === 'select') {
      const out = this.valueProp ? this.selected?.[this.valueProp] : (this.selected ?? null);
      const payload = this.valueProp ? this.selected : out;
      this.confirm.emit(payload);
      this.confirmed.emit(payload);
      this.selectRef?.close();
    } else {
      this.confirm.emit(this.textValue);
      this.confirmed.emit(this.textValue);
    }
  }

  onCancel(): void {
    this.selectRef?.close();
    this.cancel.emit();
    this.cancelled.emit();
  }

  displayOf(item: any): string {
    if (item == null) return '';
    return this.displayProp ? String(item?.[this.displayProp] ?? '') : String(item);
  }
}
